/**
 * Sense → Today — slice 62. The ranked queue of things worth doing, and the
 * ledger of what was done about them.
 *
 * ## A FINDING ENDS IN AN ACTION
 *
 * The concept's chain is signal → diagnosis → prescription → one click →
 * measured. A finding here is the first three in one row: what is true, why it
 * matters in rupees where that can be said, and one action through an endpoint
 * that already exists — open a segment, open a campaign, save an audience.
 * Nothing on this screen sends: the furthest an action goes is a prefilled form.
 *
 * ## RANKED BY ₹ AT STAKE × TRUST, AND "NOT RANKED YET" IS A BAND, NOT ₹0
 *
 * ₹ at stake is the money consequence of the finding over {@link TODAY_WINDOW_DAYS}
 * days, from this workspace's own baselines, with the arithmetic one tap away
 * (`atStake.formula`). The queue orders by that figure discounted by how much
 * its tier can be trusted ({@link TIER_WEIGHT}); the screen shows the raw rupees
 * and the tier side by side, so the order stays auditable. A finding with no
 * baseline — no list of who converted covers the window, or the finding is not
 * about people — has `atStake: null` and sits in a separate band below the
 * ranked ones. Drawing it as ₹0 would rank it last and say it is worthless,
 * when the truth is that nobody can yet say what it is worth.
 *
 * ## THE TIERS
 *
 * The owner's four, each with a weight (decision 5 of the programme):
 * matched 1.0 (a person on a day, or a holdout's measured result), coded 0.8,
 * correlated 0.4 (a baseline applied to a group — "people like them converted
 * for ₹X each"), declared 0.25. Slice 62's findings are matched or correlated;
 * coded arrives with promo codes and links (slice 63).
 *
 * ## THE LEDGER
 *
 * Taking or dismissing a finding writes one append-only row: what it was, the
 * metric at that moment, and who. The finding then leaves the queue for
 * {@link TODAY_QUIET_DAYS} days. After {@link TODAY_WINDOW_DAYS} days the ledger
 * shows the same metric again beside the baseline — before and after, computed
 * on read.
 */
import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from '../common.js';
import type { Feature } from '../enums.js';
import { flowTemplateIdSchema } from './flow.js';
import { opportunityKindSchema, saveableOpportunityKindSchema } from './opportunity.js';

/** One window for the whole queue. */
export const TODAY_WINDOW_DAYS = 30;
/** How long a taken or dismissed finding stays out of the queue. */
export const TODAY_QUIET_DAYS = 30;

export const ATTRIBUTION_TIER_VALUES = ['matched', 'coded', 'correlated', 'declared'] as const;
export const attributionTierSchema = z.enum(ATTRIBUTION_TIER_VALUES);
export type AttributionTier = z.infer<typeof attributionTierSchema>;

/** How much each tier's rupees count when ranking. The screen shows raw rupees; this only orders. */
export const TIER_WEIGHT: Readonly<Record<AttributionTier, number>> = {
  matched: 1,
  coded: 0.8,
  correlated: 0.4,
  declared: 0.25,
};

export const ATTRIBUTION_TIER_LABEL: Readonly<Record<AttributionTier, string>> = {
  matched: 'Matched',
  coded: 'Coded',
  correlated: 'Correlated',
  declared: 'Declared',
};

/** Kinds of finding. Overview opportunities keep their own kind names, prefixed. */
export const FINDING_KIND_VALUES = [
  'critical_pressure_segment',
  'holdout_no_lift',
  'opportunity_never_messaged',
  'opportunity_dormant_audience',
  'opportunity_unused_template',
  'opportunity_segment_never_sent',
  // Slice 70 — Sense ↔ Flow. The first two propose a flow (action `flow_draft`);
  // the third reads a live flow's own holdout.
  'flow_engaged_not_converted',
  'flow_leads_not_replying',
  'flow_holdout_no_lift',
] as const;
export const findingKindSchema = z.enum(FINDING_KIND_VALUES);
export type FindingKind = z.infer<typeof findingKindSchema>;

export const findingAtStakeSchema = z
  .object({
    /** Integer paise. Never negative: a finding's stake is a magnitude. */
    valueMinor: z.number().int().min(0),
    tier: attributionTierSchema,
    /** The arithmetic, in words a reader can check: "37 people × ₹412 each …". */
    formula: z.string().min(1),
  })
  .strict();
export type FindingAtStake = z.infer<typeof findingAtStakeSchema>;

export const findingActionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('link'), label: z.string().min(1), href: z.string().min(1) }).strict(),
  /** Save an Overview audience as a cohort — the Overview's own endpoint. */
  z.object({ kind: z.literal('save'), label: z.string().min(1), opportunity: saveableOpportunityKindSchema }).strict(),
  /**
   * Slice 70: create a DRAFT flow from a template — `POST /flows` with
   * `{ template, audienceSegmentId }`. `segmentId` is null from both of today's
   * detectors (neither has a segment that holds the people it counted), so the
   * draft has no audience: the person chooses who it is for in the editor.
   * Slice 70 said "prefilled with the segment the finding is about", which no
   * detector did (slice 71 review). Never launches: the person launches it.
   */
  z
    .object({
      kind: z.literal('flow_draft'),
      label: z.string().min(1),
      template: flowTemplateIdSchema,
      segmentId: uuidSchema.nullable(),
    })
    .strict(),
]);
export type FindingAction = z.infer<typeof findingActionSchema>;

/** The number a finding is about, so the ledger can show it again later. */
export const findingMetricSchema = z
  .object({
    label: z.string().min(1),
    value: z.number(),
  })
  .strict();
export type FindingMetric = z.infer<typeof findingMetricSchema>;

export const findingSchema = z
  .object({
    /** Stable for the same subject: `kind:subjectId`, or `kind:workspace`. */
    fingerprint: z.string().min(1).max(200),
    kind: findingKindSchema,
    /** One sentence: what is true. */
    title: z.string().min(1),
    /** Where it comes from, in a few words — the Overview tiles' `source`. */
    source: z.string().min(1),
    metric: findingMetricSchema,
    /** Null: not ranked yet — see the header. */
    atStake: findingAtStakeSchema.nullable(),
    action: findingActionSchema,
  })
  .strict();
export type Finding = z.infer<typeof findingSchema>;

/** `GET /sense/today`. */
export const todaySchema = z
  .object({
    windowDays: z.literal(TODAY_WINDOW_DAYS),
    generatedAt: isoDateTimeSchema,
    /** Ranked by ₹ at stake × tier weight, highest first. */
    ranked: z.array(findingSchema),
    /** Findings with no ₹ figure yet, in their detectors' own order. */
    unranked: z.array(findingSchema),
    /** Findings taken or dismissed in the last TODAY_QUIET_DAYS days, kept off the queue. */
    quiet: z.number().int().min(0),
    /**
     * Whether a list of who converted covers the window — so person-shaped
     * findings are priced. The "not ranked yet" band explains itself from it:
     * with a list, what is left unranked is not about people; without one, an
     * upload would rank them.
     */
    baselineCovered: z.boolean(),
  })
  .strict();
export type Today = z.infer<typeof todaySchema>;

/* -------------------------------------------------------------------------- */
/* The ledger                                                                  */
/* -------------------------------------------------------------------------- */

export const SENSE_DECISION_VALUES = ['taken', 'dismissed'] as const;
export const senseDecisionSchema = z.enum(SENSE_DECISION_VALUES);
export type SenseDecision = z.infer<typeof senseDecisionSchema>;

/** `POST /sense/actions`. The server re-reads the finding; the client sends only which and what. */
export const recordSenseActionRequestSchema = z
  .object({ fingerprint: z.string().min(1).max(200), decision: senseDecisionSchema })
  .strict();
export type RecordSenseActionRequest = z.infer<typeof recordSenseActionRequestSchema>;

/**
 * The same metric, now — or why there is none yet. Discriminated, so "not due"
 * and "no longer measurable" can never be drawn as a number.
 */
export const senseActionAfterSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('due'), dueOn: isoDateTimeSchema }).strict(),
  z.object({ state: z.literal('measured'), value: z.number() }).strict(),
  z.object({ state: z.literal('not_comparable'), reason: z.string() }).strict(),
]);
export type SenseActionAfter = z.infer<typeof senseActionAfterSchema>;

export const senseActionSchema = z
  .object({
    id: uuidSchema,
    fingerprint: z.string(),
    kind: findingKindSchema,
    decision: senseDecisionSchema,
    /** The finding's sentence as it read when the decision was made. */
    title: z.string(),
    metric: findingMetricSchema,
    atStake: findingAtStakeSchema.nullable(),
    decidedAt: isoDateTimeSchema,
    /** Null when the person who decided has since been removed. */
    decidedBy: z.string().nullable(),
    after: senseActionAfterSchema,
  })
  .strict();
export type SenseAction = z.infer<typeof senseActionSchema>;

/** How many ledger rows the page lists, newest first. */
export const SENSE_ACTIONS_LIMIT = 100;

export const senseActionsSchema = z
  .object({ actions: z.array(senseActionSchema).max(SENSE_ACTIONS_LIMIT) })
  .strict();
export type SenseActions = z.infer<typeof senseActionsSchema>;

/** The Overview opportunity kind a finding kind folds in, or null. */
export const FINDING_OPPORTUNITY: Readonly<Record<FindingKind, z.infer<typeof opportunityKindSchema> | null>> = {
  critical_pressure_segment: null,
  holdout_no_lift: null,
  opportunity_never_messaged: 'never_messaged',
  opportunity_dormant_audience: 'dormant_audience',
  opportunity_unused_template: 'unused_template',
  opportunity_segment_never_sent: 'segment_never_sent',
  flow_engaged_not_converted: null,
  flow_leads_not_replying: null,
  flow_holdout_no_lift: null,
};

/**
 * The pillar a finding hands its action to — slice 65.
 *
 * Sense finds; another pillar acts. Today shows this beside each finding's
 * action ("Connect"), so a person can see which part of the product the click
 * will take them into before they press it. Every current kind maps to
 * `connect`: each one ends in a segment, a campaign or a saved audience, which
 * are owned-channel work. A finding about an ad would map to `reach`; none
 * exists yet, and a total `Record` means the day one is added to
 * {@link FINDING_KIND_VALUES} this map stops compiling until somebody says
 * which pillar it belongs to.
 *
 * Typed against `Feature`, not a narrower union. Until slice 70 this said Flow
 * was not built and nothing should hand work to it; that is superseded. The
 * three flow kinds map to `flow`: two end in a draft flow, and the third is
 * about a live flow's own holdout, so the click lands in the Flow pillar.
 */
export const FINDING_PILLAR: Readonly<Record<FindingKind, Feature>> = {
  critical_pressure_segment: 'connect',
  holdout_no_lift: 'connect',
  opportunity_never_messaged: 'connect',
  opportunity_dormant_audience: 'connect',
  opportunity_unused_template: 'connect',
  opportunity_segment_never_sent: 'connect',
  flow_engaged_not_converted: 'flow',
  flow_leads_not_replying: 'flow',
  flow_holdout_no_lift: 'flow',
};
